import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2, AlertCircle, LayoutDashboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DEALERSHIP_INFO } from '../config/dealership';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLoginPage = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/dashboard/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: password.trim() }),
      });

      // If response is not ok (401), it's an invalid password
      if (!response.ok) {
        throw new Error('Invalid password');
      }

      // Try to parse the response
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        // If body was consumed by monitoring scripts, but status was OK,
        // treat it as success with no data available
        if (response.ok) {
          throw new Error('Unexpected response format');
        }
        throw new Error('Invalid password');
      }

      if (data.ok && data.token) {
        // Store token in sessionStorage
        sessionStorage.setItem('dashboard_token', data.token);
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      // Show clean error message regardless of technical details
      if (err.message.includes('body') || err.message.includes('clone') || err.message.includes('stream')) {
        setError('Invalid password');
      } else {
        setError(err.message || 'Failed to login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020405] pt-20 flex items-center justify-center px-6" data-testid="dashboard-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#0A0F14] border border-white/10 p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[#48CCCD]/20 mb-4">
              <LayoutDashboard size={32} className="text-[#48CCCD]" />
            </div>
            <h1 
              className="font-serif text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2"
              data-testid="login-title"
            >
              Dashboard Access
            </h1>
            <p className="text-sm text-[#94A3B8] font-sans">
              Enter password to access the dealer dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 text-red-400"
                data-testid="login-error"
              >
                <AlertCircle size={20} className="shrink-0" />
                <p className="text-sm font-sans">{error}</p>
              </motion.div>
            )}

            {/* Password Input */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter dashboard password"
                  className="bg-[#16202A] border-white/10 text-[#F8FAFC] h-12 pl-10 focus:border-[#48CCCD]"
                  data-testid="login-password-input"
                  autoFocus
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock size={18} className="mr-2" />
                  Unlock Dashboard
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-xs text-[#94A3B8] text-center mt-6 font-sans">
            Protected area for {DEALERSHIP_INFO.name} staff only
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardLoginPage;
